/**
 * Pure phasing arithmetic for the cashflow phasing dialog.
 *
 * Percentages are kept as the strings the user typed and summed as fixed-point
 * integers (SCALE decimal places), so "33.333333 + 66.666667" lands on exactly
 * 100 rather than float noise. The server applies the same 0.0001 tolerance.
 */

export const SCALE = 9;
const UNIT = 10 ** SCALE;
const HUNDRED_UNITS = 100 * UNIT;
export const TOLERANCE = 0.0001;
const TOLERANCE_UNITS = Math.round(TOLERANCE * UNIT);

/**
 * A decimal string or number as an integer count of 10^-SCALE units. Digits
 * beyond SCALE are truncated; anything unparseable is 0.
 */
export function toUnits(value) {
    if (value === null || value === undefined) return 0;
    let text = String(value).trim();
    if (text === '') return 0;
    if (/e/i.test(text)) {
        const num = Number(text);
        if (!Number.isFinite(num)) return 0;
        text = num.toFixed(SCALE);
    }
    const match = /^(-)?(\d*)(?:\.(\d*))?$/.exec(text);
    if (!match || `${match[2] || ''}${match[3] || ''}` === '') return 0;
    const whole = Number(match[2] || '0');
    const fraction = Number(`${match[3] || ''}`.padEnd(SCALE, '0').slice(0, SCALE));
    const units = whole * UNIT + fraction;
    return match[1] ? -units : units;
}

/**
 * Units back to a trimmed decimal string ("33.5", "100").
 */
export function fromUnits(units) {
    const negative = units < 0;
    const abs = Math.abs(Math.round(units));
    const whole = Math.floor(abs / UNIT);
    const fraction = String(abs % UNIT)
        .padStart(SCALE, '0')
        .replace(/0+$/, '');
    return `${negative ? '-' : ''}${whole}${fraction ? `.${fraction}` : ''}`;
}

export function sumUnits(rows) {
    return rows.reduce((total, row) => total + toUnits(row.percentage), 0);
}

/**
 * Whether a schedule allocates exactly 100% (within TOLERANCE).
 */
export function isExactly100(rows) {
    return rows.length > 0 && Math.abs(sumUnits(rows) - HUNDRED_UNITS) < TOLERANCE_UNITS;
}

const DECIMAL_PATTERN = /^-?(\d+(\.\d*)?|\.\d+)$/;

/**
 * Why a row's percentage cannot be saved, or null when it can. Blank and 0 are
 * allowed: those rows are dropped on save.
 */
export function rowPercentageError(value) {
    const text = String(value ?? '').trim();
    if (text === '') return null;
    if (!DECIMAL_PATTERN.test(text)) return 'invalid';
    const units = toUnits(text);
    if (units < 0) return 'negative';
    if (units > HUNDRED_UNITS) return 'over';
    return null;
}

export function rowsInRange(rows) {
    return rows.every((row) => rowPercentageError(row.percentage) === null);
}

/** The rows a save sends: every month carrying more than 0%. */
export function withoutZeroRows(rows) {
    return rows.filter((row) => toUnits(row.percentage) !== 0);
}

/**
 * `month` moved into `periods`: before the first period → the first, after the
 * last → the last. Null when there are no periods.
 */
export function clampMonth(month, periods) {
    if (!periods.length) return month || null;
    const sorted = [...periods].sort(compareMonths);
    if (!month) return sorted[0];
    if (compareMonths(month, sorted[0]) < 0) return sorted[0];
    if (compareMonths(month, sorted[sorted.length - 1]) > 0) return sorted[sorted.length - 1];
    return month;
}

/** Split `items` into consecutive batches of at most `size`. */
export function chunk(items, size) {
    const batches = [];
    for (let index = 0; index < items.length; index += size) {
        batches.push(items.slice(index, index + size));
    }
    return batches;
}

/**
 * Allocated and remaining percentage points as numbers, for display.
 */
export function allocation(rows) {
    const allocated = sumUnits(rows);
    return {
        allocated: allocated / UNIT,
        remaining: (HUNDRED_UNITS - allocated) / UNIT,
    };
}

// ─── Months ('MM-YYYY') ─────────────────────────────────────────────────

function monthIndex(month) {
    const [mm, yyyy] = String(month).split('-');
    return Number(yyyy) * 12 + (Number(mm) - 1);
}

export function addMonths(month, count) {
    const index = monthIndex(month) + count;
    const year = Math.floor(index / 12);
    const mm = String((index % 12) + 1).padStart(2, '0');
    return `${mm}-${year}`;
}

export function compareMonths(a, b) {
    return monthIndex(a) - monthIndex(b);
}

export function sortRows(rows) {
    return [...rows].sort((a, b) => compareMonths(a.month, b.month));
}

/**
 * Split 100% into `count` equal parts at SCALE precision, with the rounding
 * residual on the final part.
 */
function evenSplit(count) {
    const base = Math.floor(HUNDRED_UNITS / count);
    return Array.from({ length: count }, (_, index) => (index === count - 1 ? HUNDRED_UNITS - base * (count - 1) : base));
}

export const PROFILE_SINGLE = 'single';
export const PROFILE_MONTHLY = 'monthly';
export const PROFILE_QUARTERLY = 'quarterly';

/**
 * Build a profile's months from `start`.
 *   single     — 100% in the start month
 *   monthly    — equal instalments over `count` consecutive months
 *   quarterly  — equal instalments every third month, `count` times
 * Months outside `periods` (past the project end) are dropped, so a truncated
 * profile falls short of 100% and the save gate asks the user to resolve it.
 */
export function buildProfile(profile, start, count, periods) {
    const instalments = profile === PROFILE_SINGLE ? 1 : Math.max(1, Math.floor(Number(count) || 1));
    const step = profile === PROFILE_QUARTERLY ? 3 : 1;
    const allowed = new Set(periods);
    const parts = evenSplit(instalments);

    return parts.map((units, index) => ({ month: addMonths(start, index * step), percentage: fromUnits(units) })).filter((row) => allowed.has(row.month));
}

/**
 * Rescale the listed months proportionally to exactly 100%, residual on the
 * last month that carries anything. A 0% month stays at 0. A schedule with
 * nothing allocated is left as it is.
 */
export function scaleTo100(rows) {
    const sorted = sortRows(rows);
    const total = sumUnits(sorted);
    if (total <= 0 || sorted.length === 0) return sorted;

    let lastIndex = sorted.length - 1;
    while (lastIndex > 0 && toUnits(sorted[lastIndex].percentage) === 0) lastIndex -= 1;

    let assigned = 0;
    return sorted.map((row, index) => {
        if (index > lastIndex) return { ...row, percentage: '0' };
        if (index === lastIndex) {
            return { ...row, percentage: fromUnits(HUNDRED_UNITS - assigned) };
        }
        const units = Math.round((toUnits(row.percentage) / total) * HUNDRED_UNITS);
        assigned += units;
        return { ...row, percentage: fromUnits(units) };
    });
}

// ─── % ⇄ value ──────────────────────────────────────────────────────────

/**
 * The value a percentage represents of `total`, at 2 decimal places.
 */
export function percentageToValue(percentage, total) {
    const value = (toUnits(percentage) / UNIT) * Number(total || 0);
    return Math.round(value) / 100;
}

/**
 * Each row's value of `total` in cents-rounded money, keyed by month. When the
 * rows allocate exactly 100%, the rounding residual goes on the last row that
 * carries anything, so the values add back to the line total.
 */
export function valuesForRows(rows, total) {
    const totalCents = Math.round(Number(total || 0) * 100);
    const values = {};
    let assigned = 0;
    let lastIndex = -1;
    rows.forEach((row, index) => {
        const cents = Math.round(((toUnits(row.percentage) / UNIT) * totalCents) / 100);
        values[row.month] = cents;
        assigned += cents;
        if (toUnits(row.percentage) !== 0) lastIndex = index;
    });
    if (lastIndex >= 0 && isExactly100(rows)) {
        values[rows[lastIndex].month] += totalCents - assigned;
    }
    Object.keys(values).forEach((month) => {
        values[month] /= 100;
    });
    return values;
}

/** A percentage for display: at most 2 decimals, trailing zeros dropped. */
export function displayPercentage(value) {
    const rounded = Math.round((toUnits(value) / UNIT) * 100) / 100;
    return String(rounded);
}

/**
 * The percentage a typed value represents of `total`, as a decimal string at
 * SCALE precision. Zero when there is no total to divide by.
 */
export function valueToPercentage(value, total) {
    const base = Number(total || 0);
    const amount = Number(String(value ?? '').replace(/,/g, ''));
    if (!Number.isFinite(amount) || base <= 0) return '0';
    return fromUnits(Math.round((amount / base) * HUNDRED_UNITS));
}
